/**
 * How to apply. Drives `sections/product-how-to.liquid`: one step plays at a
 * time and each hands on to the next when it ends, the last looping back to
 * the first (agreed 24 Sep, REQUIREMENTS, PDP).
 *
 *   mobile   the active step's own tile plays
 *   desktop  the stage beside the list plays; the tiles hold their first frame
 *
 * A step holding a photo shows for `data-image-seconds`, then moves on. The
 * chain only runs while the section is on screen, starts muted and inline so
 * every browser allows it, and does not start by itself under reduced motion.
 * Choosing a step plays it. The playing step's progress is drawn in its
 * `[data-how-to-progress]` bar, every frame while a video plays.
 *
 * Section-scoped, idempotent, re-run on `shopify:section:load`.
 */
(function () {
  var DESKTOP = window.matchMedia('(min-width: 768px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(section) {
    if (section.dataset.howToReady === 'true') return;
    section.dataset.howToReady = 'true';

    var steps = Array.prototype.slice.call(section.querySelectorAll('[data-how-to-step]'));
    var layers = Array.prototype.slice.call(section.querySelectorAll('[data-how-to-layer]'));
    if (!steps.length) return;

    var imageMs = (Number(section.dataset.imageSeconds) || 4) * 1000;
    var current = 0;
    var visible = false;
    var started = false;
    var timer = null;
    var imageStart = 0;
    var frame = null;

    function player(i) {
      if (DESKTOP.matches) {
        var layer = layers[i];
        return layer ? layer.querySelector('video') : null;
      }
      return steps[i].querySelector('[data-how-to-thumb]');
    }

    function kind(i) {
      return steps[i].dataset.kind || 'none';
    }

    function allVideos() {
      return section.querySelectorAll('video');
    }

    function setProgress(i, ratio) {
      steps.forEach(function (step, n) {
        var bar = step.querySelector('[data-how-to-progress]');
        if (!bar) return;
        bar.style.transform = 'scaleX(' + (n === i ? Math.max(0, Math.min(1, ratio)) : 0) + ')';
      });
    }

    function stopAll() {
      window.clearTimeout(timer);
      timer = null;
      if (frame) window.cancelAnimationFrame(frame);
      frame = null;
      allVideos().forEach(function (video) {
        if (!video.paused) video.pause();
      });
    }

    /* A video's own `timeupdate` fires about four times a second, which
     * moves the bar in visible steps (Tim, 5 Oct: "juddery"). While the
     * active video plays, the bar is drawn every frame from its playhead. */
    function tickVideo() {
      var video = player(current);
      if (!video || video.paused || video.ended || !video.duration) {
        frame = null;
        return;
      }
      setProgress(current, video.currentTime / video.duration);
      frame = window.requestAnimationFrame(tickVideo);
    }

    function tickImage() {
      var ratio = (Date.now() - imageStart) / imageMs;
      setProgress(current, ratio);
      if (ratio < 1) frame = window.requestAnimationFrame(tickImage);
    }

    function show(i) {
      current = i;
      steps.forEach(function (step, n) {
        var on = n === i;
        step.toggleAttribute('data-active', on);
        step.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      layers.forEach(function (layer, n) {
        layer.hidden = n !== i;
      });
      setProgress(i, 0);
    }

    function next() {
      play((current + 1) % steps.length);
    }

    function play(i) {
      stopAll();
      show(i);
      started = true;

      var k = kind(i);
      if (k === 'video') {
        var video = player(i);
        if (!video) return;
        video.preload = 'auto';
        try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
        var attempt = video.play();
        if (attempt && attempt.catch) {
          attempt.catch(function () {
            /* Autoplay refused (low power mode, data saver): leave the poster
             * showing and wait for a tap rather than skipping ahead. */
          });
        }
      } else if (steps.length > 1) {
        /* A photo, or a step with no media: hold, then hand on. */
        imageStart = Date.now();
        frame = window.requestAnimationFrame(tickImage);
        timer = window.setTimeout(next, imageMs);
      }
    }

    /* Every video reports its own progress and end; only the active player's
     * events count, so a stray tile finishing does not move the chain. */
    allVideos().forEach(function (video) {
      video.addEventListener('playing', function () {
        if (video !== player(current)) return;
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(tickVideo);
      });
      /* A paused or seeked video still shows where it is. */
      video.addEventListener('timeupdate', function () {
        if (video !== player(current) || !video.duration || frame) return;
        setProgress(current, video.currentTime / video.duration);
      });
      video.addEventListener('ended', function () {
        if (video !== player(current)) return;
        setProgress(current, 1);
        next();
      });
    });

    steps.forEach(function (step, n) {
      step.addEventListener('click', function () {
        play(n);
      });
    });

    /* Run only while on screen. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible = entry.isIntersecting;
          if (visible) {
            if (!started && !REDUCED.matches) play(current);
            else if (started) resume();
          } else {
            stopAll();
          }
        });
      }, { threshold: 0.25 }).observe(section);
    }

    function resume() {
      if (kind(current) === 'video') {
        var video = player(current);
        if (video && video.paused) video.play().catch(function () {});
      } else {
        play(current);
      }
    }

    /* Crossing the breakpoint swaps which element plays. */
    var onChange = function () {
      if (!started) return;
      stopAll();
      if (visible) play(current);
    };
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onChange);

    show(0);
  }

  function run(scope) {
    (scope || document).querySelectorAll('[data-how-to]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); });
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', function (event) {
    run(event.target);
  });
})();
